import {
  Box,
  Chip,
  Grid,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import visuallyHidden from '@mui/utils/visuallyHidden';
import { countBy, find, isEmpty, isNil, map, toLower } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import PackSizeText from 'common/components/PackSizeText';
import FacilityAvatar from 'common/modules/FacilityAvatar';
import { casingFormat } from 'common/utils';
import formatDrugLabel from 'common/utils/formatDrugLabel';
import { SortOrder } from 'enums/common.enum';
import { FILL_LATER_PAGE_SIZE } from 'api/fillLater';
import { listFillLaterByPage, updateFillLaterSort } from 'store/thunks/workQueueThunks';

import { FILL_LATER_ACTIONABLE_STATUSES } from '../constants/fillLaterEligibility';
import { formatScheduledDate, relativeScheduledText } from '../utils/fillLaterDates';
import FillLaterAddressCell from './FillLaterAddressCell';
import FillLaterQueueOptions from './FillLaterQueueOptions';
import FillLaterQueueStatusChip from './FillLaterQueueStatusChip';
import FillLaterQueueTypeChip from './FillLaterQueueTypeChip';

// Column order follows the Bulk tab. Sort keys match FillLaterItemsSortKey on the backend.
// Every column is left-aligned, as in Bulk and Central Fill: a right-aligned Qty sat flush against
// the Scheduled For date (POM-6149 feedback).
const headCells = [
  { id: 'orderId', label: 'Source Order Id', sortable: true },
  { id: 'fillLaterType', label: 'Type', sortable: false },
  { id: 'patientName', label: 'Patient', sortable: true },
  { id: 'drugName', label: 'Drug Name', sortable: true },
  // Not sortable: listFillLaterItems has no ndc sort key (same as every other work queue).
  { id: 'ndc', label: 'NDC', sortable: false },
  { id: 'rxNumber', label: 'Rx Number', sortable: true },
  { id: 'quantity', label: 'Qty', sortable: true },
  { id: 'scheduledFor', label: 'Scheduled For', sortable: true },
  { id: 'shippingTo', label: 'Shipping To', sortable: false },
  { id: 'partnerName', label: 'Partner', sortable: true },
  { id: 'practiceName', label: 'Practice', sortable: true },
  { id: 'px', label: 'Px', sortable: false },
  { id: 'status', label: 'Status', sortable: true },
];

const FillLaterQueueTable = () => {
  const dispatch = useDispatch();
  const { sortBy, pages, currentPage, count } = useSelector(({ workQueue }) => workQueue.fillLater);
  const currentTenantId = useSelector(({ auth }) => auth?.currentTenantId);
  const facilities = useSelector(({ pharmacy }) => pharmacy?.[currentTenantId]);

  const totalPages = Math.ceil(count / FILL_LATER_PAGE_SIZE);
  const rows = pages[currentPage];

  // Each row says "n of m scheduled" under its Order Id so several splits of one original read as
  // a set. Counted over the current page: sorting is flat, so siblings are no longer guaranteed
  // adjacent and a split whose sibling sits on another page reads as "1 of 1" here.
  const groupSizes = countBy(rows, 'referenceOrderId');
  const groupIndex = {};

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;

    dispatch(
      updateFillLaterSort({
        sortBy: {
          sortKey,
          sortOrder:
            sortBy.sortKey === sortKey && sortBy.sortOrder === SortOrder.ASCENDING
              ? SortOrder.DESCENDING
              : SortOrder.ASCENDING,
        },
      })
    );
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) return;
    dispatch(listFillLaterByPage({ page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Grid container direction='column'>
      <Grid item>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell key={headCell.id}>
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={sortBy.sortKey === headCell.id}
                        direction={
                          sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : 'asc'
                        }
                        data-head-property={headCell.id}
                        onClick={handleSort}
                      >
                        {headCell.label}
                        {sortBy.sortKey === headCell.id ? (
                          <Box component='span' sx={visuallyHidden}>
                            {toLower(sortBy.sortOrder) === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {map(rows, (row) => {
                const groupSize = groupSizes[row.referenceOrderId] ?? 1;
                groupIndex[row.referenceOrderId] = (groupIndex[row.referenceOrderId] ?? 0) + 1;
                const positionInGroup = groupIndex[row.referenceOrderId];

                const isActionable = FILL_LATER_ACTIONABLE_STATUSES.has(row.status);
                const relative = isActionable ? relativeScheduledText(row.scheduledFor) : null;

                const facility = find(facilities, {
                  fulfillingPharmacyId: row.fulfillingPharmacyId,
                });
                const rxPrefix = facility?.config?.rxPrefix;

                return (
                  <TableRow
                    key={row.fillLaterItemId}
                    hover
                    sx={
                      relative?.overdue
                        ? { '& > td:first-of-type': { boxShadow: 'inset 3px 0 0 #FFC107' } }
                        : undefined
                    }
                  >
                    <TableCell component='th' scope='row'>
                      <Link to={`/orders/${row.referenceOrderId}`} style={{ color: 'inherit' }}>
                        {row.referenceOrderId}
                      </Link>
                      <Typography variant='caption' color='textSecondary' display='block'>
                        {`${positionInGroup} of ${groupSize} scheduled`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <FillLaterQueueTypeChip fillLaterType={row.fillLaterType} />
                    </TableCell>
                    <TableCell>
                      {row.patientId && row.patientName ? (
                        <Link to={`/patients/${row.patientId}`} style={{ color: 'inherit' }}>
                          {row.patientName}
                        </Link>
                      ) : (
                        row.patientName || 'N/A'
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        <Box component='span'>{formatDrugLabel(row) || 'N/A'}</Box>
                        {row.isOtc && (
                          <Chip
                            size='small'
                            variant='outlined'
                            color='info'
                            label='OTC'
                            sx={{
                              ml: 0.5,
                              height: 14,
                              fontWeight: 600,
                              '& .MuiChip-label': { px: 0.5, fontSize: '0.5rem' },
                            }}
                          />
                        )}
                      </Box>
                      <PackSizeText
                        packSizeValue={row.packSizeValue}
                        packSizeUnit={row.packSizeUnit}
                        formAlias={row.formAlias}
                      />
                    </TableCell>
                    {/* An NDC is only bound at fulfilment, so a scheduled split may not have one */}
                    <TableCell>{row.ndc || '-'}</TableCell>
                    <TableCell>{row.rxNumber || 'N/A'}</TableCell>
                    <TableCell sx={{ width: 80 }}>{row.splitQuantity}</TableCell>
                    <TableCell sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>
                      {formatScheduledDate(row.scheduledFor)}
                      {relative && (
                        <Typography
                          variant='caption'
                          display='block'
                          sx={{
                            color: relative.overdue ? 'text.dark' : 'text.secondary',
                            fontWeight: relative.overdue ? 600 : 400,
                          }}
                        >
                          {relative.text}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <FillLaterAddressCell
                        address={row.address}
                        addressDirectoryId={row.addressDirectoryId}
                      />
                    </TableCell>
                    <TableCell>{casingFormat(row.partnerName) || 'N/A'}</TableCell>
                    <TableCell>{casingFormat(row.practiceName) || 'N/A'}</TableCell>
                    <TableCell>
                      {rxPrefix ? (
                        <FacilityAvatar
                          rxPrefix={rxPrefix}
                          facilityName={facility.name}
                          facilityState={facility.state}
                        />
                      ) : (
                        <Grid />
                      )}
                    </TableCell>
                    <TableCell>
                      <FillLaterQueueStatusChip status={row.status} />
                      {row.status === 'FAILED' && row.failureReason && (
                        <Tooltip title={row.failureReason} arrow>
                          <Typography
                            variant='caption'
                            color='error'
                            display='block'
                            sx={{ maxWidth: 300, mt: 0.5, lineHeight: 1.3 }}
                          >
                            {row.failureReason}
                          </Typography>
                        </Tooltip>
                      )}
                      {row.status === 'CREATED' && row.splitOrderId && (
                        <Typography variant='caption' color='textSecondary' display='block'>
                          New order{' '}
                          <Link to={`/orders/${row.splitOrderId}`} style={{ color: 'inherit' }}>
                            {row.splitOrderId}
                          </Link>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <FillLaterQueueOptions item={row} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>

            {!isNil(rows) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={headCells.length + 1}
                    align='right'
                    sx={{ borderBottom: 'none' }}
                  >
                    <Pagination
                      sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(rows) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(rows) && isEmpty(rows) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

export default FillLaterQueueTable;
